import React from 'react';
import { useTranslation } from 'react-i18next';
import CopyableText from 'components/atoms/buttons/copyable-text';
import Status from 'components/atoms/status';
import { differenceInCalendarDays } from 'date-fns';
import common from 'constants/common';
import { parseDateSafe } from 'utils/timeHelper';
import DisabledIcon from 'components/atoms/icons/disabledIcon';
import CheckCircleOutlined from 'components/atoms/icons/checkCircleOutlined';

import s from './styles.module.scss';

import type { IPatientDetails } from 'infrastructure/hooks/smsDashboard/usePatientInfo';

interface IProps {
  patientInfo: IPatientDetails | undefined;
}

const AccountInfo: React.FC<IProps> = ({ patientInfo }) => {
  const { t } = useTranslation();

  const guid = `${patientInfo?.profile.guid}`;

  const calculateHoldingStatus = (holdingStatus: {
    status: string;
    endTime: string;
  }) => {
    const label = (common.patientHoldingStatus as { [key: string]: string })[
      holdingStatus.status
    ];

    if (
      holdingStatus.status === 'hospital' ||
      holdingStatus.status === 'vacation'
    ) {
      const today = new Date();
      const endDate = parseDateSafe(holdingStatus?.endTime);
      const remainingDays =
        endDate && differenceInCalendarDays(new Date(endDate), today);

      return `${label} ( ${(remainingDays as number) + 1} days )`;
    }
    return label;
  };

  const info = [
    {
      label: t('smsDashboard.id'),
      value: (
        <CopyableText value={guid} className={s['chat-profile__copy-id']} />
      ),
    },
    {
      label: t('smsDashboard.rpm_status'),
      value: (
        <Status
          status={patientInfo?.profile.status}
          category="statusPatients"
        />
      ),
    },
    {
      label: t('smsDashboard.ссm_status'),
      value: (
        <Status status={patientInfo?.profile.ccmStatus} category="ccmStatus" />
      ),
    },
    {
      label: t('smsDashboard.pcm_status'),
      value: (
        <Status
          status={`${patientInfo?.profile.pcmStatus}`}
          category="pcmStatusNames"
        />
      ),
    },
    {
      label: t('smsDashboard.holding_status'),
      value:
        patientInfo?.profile.holdingStatus &&
        calculateHoldingStatus(patientInfo?.profile.holdingStatus),
    },
    {
      label: t('smsDashboard.rpm_monthly_interaction'),
      value: (
        <Status
          status={`${patientInfo?.profile.interactionStatus}`}
          category="pcmStatusNames"
        />
      ),
    },
    {
      label: t('smsDashboard.call_availability'),
      value: (
        <div className={s.flex}>
          {patientInfo?.profile.doNotCallRestrictionType ? (
            <DisabledIcon />
          ) : (
            <CheckCircleOutlined width="16" height="16" />
          )}
          <Status
            status={`${patientInfo?.profile.doNotCallRestrictionType}`}
            category="doNotCallStatusNames"
          />
        </div>
      ),
    },
  ];
  return (
    <div className={s.wrapper}>
      {info.map((el, i) => (
        <div key={i} className={s.item}>
          <p>{el.label}</p>
          <div>{el.value}</div>
        </div>
      ))}
    </div>
  );
};

export default AccountInfo;
